import { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { supabase } from '../supabaseClient';

// Define the role types
export type Role = 'super_admin' | 'admin' | 'auditor' | 'field_agent' | 'manager' | null;

// Define the permission types - these match the permissions in the database
export type Permission =
	| 'submissions.create'
	| 'submissions.read'
	| 'submissions.update'
	| 'submissions.delete'
	| 'profiles.read'
	| 'profiles.update'
	| 'profiles.delete'
	| 'logs.read'
	| 'logs.create'
	| 'users.read'
	| 'users.read'
	| 'users.create'
	| 'users.read'
	| 'users.delete';

// JWT structure with role and permissions
interface JWTPayload {
	sub: string;
	aud: string;
	exp: number;
	user_role?: Role;
	user_permissions?: Permission[];
	[key: string]: any;
}

/**
 * Custom hook for role-based access control using JWT claims
 */
export const useRBAC = () => {
	const [role, setRole] = useState<Role>(null);
	const [permissions, setPermissions] = useState<Permission[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Define role hierarchy for permission checks
	const roleHierarchy: Record<string, number> = {
		super_admin: 5,
		admin: 4,
		manager: 3,
		auditor: 2,
		field_agent: 1,
	};

	// Load role and permissions from JWT token only
	useEffect(() => {
		const loadRoleAndPermissions = async () => {
			setLoading(true);
			setError(null);

			try {
				const {
					data: { session },
				} = await supabase.auth.getSession();

				if (!session) {
					setRole(null);
					setPermissions([]);
					setLoading(false);
					return;
				}

				// Extract role and permissions from JWT token
				const token = session.access_token;
				const decoded = jwtDecode<JWTPayload>(token);

				// console.log(decoded);

				// Set role from token
				const userRole = decoded.user_role || null;
				setRole(userRole);

				// Set permissions from token
				const userPermissions = decoded.user_permissions || [];
				setPermissions(userPermissions);
			} catch {
				setError('Failed to load user role and permissions from token');
			} finally {
				setLoading(false);
			}
		};

		loadRoleAndPermissions();

		// Subscribe to auth changes to update role and permissions
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange(() => {
			loadRoleAndPermissions();
		});

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	// Check if user has a specific permission
	const hasPermission = (permission: Permission): boolean => {
		return permissions.includes(permission);
	};

	// Check if user has at least the specified role level
	const isAtLeastRole = (minRole: Role): boolean => {
		if (!role || !minRole) return false;
		return roleHierarchy[role] >= roleHierarchy[minRole];
	};

	return {
		role,
		permissions,
		hasPermission,
		isAtLeastRole,
		loading,
		error,
	};
};

export default useRBAC;
